import type { ReactNode } from "react"
import { cn } from "@/lib/utils"
import { roughRect, roughEllipse, roughArrow, roughUnderline, roughLine } from "./rough"
/**
 * The drawn layer, ported from the marketing site.
 *
 * The app's typeface does NOT change — the founder reads their own money and
 * their own filings in Geist, and handwriting is the wrong face for that. What
 * comes across is the hand: drawn frames instead of CSS borders, and `<Scribble>`
 * for asides. Used on tips, empty states and pointers only. A dashboard someone
 * works in all day must not become a notebook.
 *
 * Colours come from the app's own shadcn tokens, so this inherits the product's
 * palette rather than importing the marketing site's paper.
 */

type Ink = "ink" | "pencil" | "marker"
const STROKE: Record<Ink, string> = {
  ink: "var(--foreground)",
  pencil: "var(--muted-foreground)",
  marker: "var(--primary)",
}
/** A hand-drawn frame around whatever you put in it. */
export function SketchBox({
  children,
  seed,
  ink = "ink",
  width = 1.9,
  className,
  fill,
  bow = 2.4,
}: {
  children: ReactNode
  seed: string
  ink?: Ink
  width?: number
  className?: string
  /** Paper tint behind the frame, e.g. "var(--muted)". */
  fill?: string
  /**
   * How crooked the hand is. The path is drawn on a 100x60 field and stretched
   * to fit, so the wobble stretches with it — a full-width panel wants roughly a
   * third of the bow a button does, or it reads as collapsed rather than drawn.
   */
  bow?: number
}) {
  const paths = roughRect(100, 60, seed, bow)
  return (
    <div className={cn("relative", className)}>
      <svg
        className="absolute inset-0 h-full w-full overflow-visible"
        viewBox="0 0 100 60"
        preserveAspectRatio="none"
        aria-hidden="true"
      >
        {fill && <rect x="1" y="1" width="98" height="58" fill={fill} />}
        {paths.map((d, i) => (
          <path
            key={i}
            d={d}
            fill="none"
            stroke={STROKE[ink]}
            strokeWidth={width}
            strokeLinecap="round"
            vectorEffect="non-scaling-stroke"
            opacity={i % 2 ? 0.55 : 1}
          />
        ))}
      </svg>
      <div className="relative">{children}</div>
    </div>
  )
}

/** A loop drawn round a word, the way you circle the number that matters. */
export function Circled({
  children,
  seed,
  ink = "marker",
  className,
}: {
  children: ReactNode
  seed: string
  ink?: Ink
  className?: string
}) {
  const paths = roughEllipse(100, 60, seed)
  return (
    // The loop needs room, and the words either side need to not be inside it.
    <span className={cn("relative mx-5 inline-block", className)}>
      <svg
        className="pointer-events-none absolute -inset-x-5 -inset-y-3 h-[calc(100%+1.5rem)] w-[calc(100%+2.5rem)] overflow-visible"
        viewBox="0 0 100 60"
        preserveAspectRatio="none"
        aria-hidden="true"
      >
        {paths.map((d, i) => (
          <path
            key={i}
            d={d}
            fill="none"
            stroke={STROKE[ink]}
            strokeWidth={2}
            strokeLinecap="round"
            vectorEffect="non-scaling-stroke"
            opacity={i ? 0.5 : 0.95}
          />
        ))}
      </svg>
      <span className="relative">{children}</span>
    </span>
  )
}

/** Two scratchy strokes under a word. */
export function Underlined({
  children,
  seed,
  ink = "marker",
  className,
}: {
  children: ReactNode
  seed: string
  ink?: Ink
  className?: string
}) {
  const paths = roughUnderline(100, seed)
  return (
    <span className={cn("relative inline-block", className)}>
      <span className="relative">{children}</span>
      <svg
        className="pointer-events-none absolute -bottom-2 left-0 h-2.5 w-full overflow-visible"
        viewBox="0 0 100 12"
        preserveAspectRatio="none"
        aria-hidden="true"
      >
        {paths.map((d, i) => (
          <path
            key={i}
            d={d}
            fill="none"
            stroke={STROKE[ink]}
            strokeWidth={2.2}
            strokeLinecap="round"
            vectorEffect="non-scaling-stroke"
            opacity={i ? 0.45 : 1}
          />
        ))}
      </svg>
    </span>
  )
}

/**
 * A margin note in the author's own hand.
 *
 * This is the voice of the site. A serious page states the price; ours states
 * the price and then leans over and says something about it. Keep them short,
 * dry and true — a quip that oversells is worse than no quip, because the whole
 * point is that this is the honest aside rather than the marketing line.
 */
export function Scribble({
  children,
  seed,
  /** Which way the little arrow leans out toward what is being commented on. */
  point = "left",
  className,
}: {
  children: ReactNode
  seed: string
  point?: "left" | "right" | "up" | "down" | "none"
  className?: string
}) {
  // Drawn on a 60x40 field so the curve has room to read as a curve. Each
  // arrow starts near the text and ends out at whatever is being pointed at.
  const geometry: Record<string, { from: [number, number]; to: [number, number]; curve: number; box: string }> = {
    left: { from: [56, 30], to: [6, 10], curve: 0.22, box: "h-8 w-14" },
    right: { from: [4, 30], to: [54, 10], curve: -0.22, box: "h-8 w-14" },
    up: { from: [30, 38], to: [16, 4], curve: 0.3, box: "h-9 w-10" },
    down: { from: [16, 4], to: [30, 38], curve: -0.3, box: "h-9 w-10" },
  }
  const g = point === "none" ? null : geometry[point]
  const paths = g ? roughArrow(g.from, g.to, seed, g.curve) : []
  const stacked = point === "up" || point === "down"
  const arrow = g ? (
    <svg
      className={cn("shrink-0 overflow-visible", g.box)}
      viewBox="0 0 60 40"
      fill="none"
      aria-hidden="true"
    >
      {paths.map((d, i) => (
        <path
          key={i}
          d={d}
          stroke={STROKE.marker}
          strokeWidth={1.8}
          strokeLinecap="round"
          vectorEffect="non-scaling-stroke"
          opacity={i > 2 ? 0.35 : 0.95}
        />
      ))}
    </svg>
  ) : null
  return (
    <span
      className={cn(
        "font-display text-[15px] leading-snug text-primary",
        stacked ? "inline-flex flex-col items-center gap-0" : "inline-flex items-start gap-1.5",
        point === "right" && "flex-row-reverse",
        point === "down" && "flex-col-reverse",
        className,
      )}
    >
      {arrow}
      <span className="-rotate-2">{children}</span>
    </span>
  )
}

/** A drawn rule, for when a section needs a break rather than a border. */
export function SketchRule({ seed, className }: { seed: string; className?: string }) {
  const paths = roughLine([0, 4], [100, 4], seed, 2.6)
  return (
    <svg
      className={cn("h-2 w-full overflow-visible text-border", className)}
      viewBox="0 0 100 8"
      preserveAspectRatio="none"
      aria-hidden="true"
    >
      {paths.map((d, i) => (
        <path
          key={i}
          d={d}
          fill="none"
          stroke="var(--border)"
          strokeWidth={2}
          strokeLinecap="round"
          vectorEffect="non-scaling-stroke"
          opacity={i ? 0.5 : 1}
        />
      ))}
    </svg>
  )
}

